/**
 * YouTube URL parsing for the videos field.
 *
 * Single owner of "URL → 11-char video id" and "id → canonical embed URL". Pure,
 * no DOM. Mirrors the legacy `editor/Editor/Video.php::extractVideoId` regex so
 * the JS and PHP sides stay in sync.
 *
 * NOTE: tiptap's `YOUTUBE_PATTERN` (rich-text link toolbar) is a different concern
 * — a prefix test for "is this a YouTube link", not an id extractor. It is left
 * as-is; this module is scoped to the videos field.
 *
 * @module
 */

/** A YouTube video id is exactly 11 chars from the URL-safe alphabet. */
const VIDEO_ID = /^[\w-]{11}$/;

/** Path-prefixes that carry the id as the next segment: `/embed/ID`, `/v/ID`, `/live/ID`, `/shorts/ID`. */
const PATH_PREFIXES = ['/embed/', '/v/', '/live/', '/shorts/'];

/**
 * Extract the 11-char YouTube video id from any common URL form, or `null`.
 *
 * Parses the FULL URL via `new URL()` and matches on the actual host + pathname (never an
 * unanchored substring of the raw string), so a non-YouTube URL that merely CONTAINS a YouTube
 * link (e.g. `https://evil.com/?u=https://youtu.be/<id>`) does NOT resolve.
 *
 * Robust to:
 * - missing scheme (`www.youtube.com/watch?v=ID`) — a scheme is prepended before `new URL()`,
 *   which otherwise throws on a scheme-less string;
 * - `www.` and `m.` (mobile) sub-hosts;
 * - `watch` URLs with `v` in any query position (`watch?feature=shared&v=ID`);
 * - path forms (`embed`, `v`, `live`, `shorts`) on `youtube.com` / `youtube-nocookie.com`, and
 *   `youtu.be/ID` (so our own `buildEmbedUrl` output round-trips).
 *
 * Returns `null` for anything that doesn't resolve to a valid 11-char id (never throws).
 *
 * @param {unknown} url
 * @returns {string | null}
 */
export function extractVideoId(url) {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  let parsed;
  try {
    parsed = new URL(/:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return null; // not a parseable URL
  }

  // Strip a leading `www.` or `m.` (mobile) sub-host so share links normalize.
  const host = parsed.hostname.replace(/^(?:www|m)\./, '');

  // youtu.be/<id> — the id is the first (only) path segment.
  if (host === 'youtu.be') {
    const id = parsed.pathname.replace(/^\//, '');
    return VIDEO_ID.test(id) ? id : null;
  }

  if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    // watch?...v=<id> — query, so v need not be first.
    if (parsed.pathname === '/watch') {
      const v = parsed.searchParams.get('v');
      return v && VIDEO_ID.test(v) ? v : null;
    }
    // /embed/<id>, /v/<id>, /live/<id>, /shorts/<id>
    for (const prefix of PATH_PREFIXES) {
      if (parsed.pathname.startsWith(prefix)) {
        const id = parsed.pathname.slice(prefix.length).split('/')[0];
        return VIDEO_ID.test(id) ? id : null;
      }
    }
  }

  return null;
}

/**
 * Build the canonical, security-hardened embed URL for a video id.
 *
 * Explicit HTTPS (no protocol inheritance) + the privacy-enhanced
 * `youtube-nocookie.com` host. The caller must pass a VALID id (use
 * `extractVideoId` first); this does not validate.
 *
 * @param {string} videoId
 * @returns {string}
 */
export function buildEmbedUrl(videoId) {
  return `https://www.youtube-nocookie.com/embed/${videoId}`;
}
